'use client'

import { useMutation, useQuery } from '@apollo/client'
import { Avatar, Box } from '@mui/material'
import { Button, Colors, Icon, Shadows, Typography } from '@ui'
import { useClickOutside } from 'app/_hooks'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { gql } from '~/__generated__'

const GET_UNREAD_FIRST_NOTIFICATION_QUERY = gql(`
  query GetUnreadFirstNotification {
    notifications(first: 1, unread: true) {
      nodes {
        id
      }
    }
  }
`)

const GET_THREE_NOTIFICATIONS_QUERY = gql(`
  query GetThreeNotifications {
    notifications(first: 3) {
      nodes {
        id
        avatarUrl
        title
        readAt
        message
        thumbnailUrl
        linkUrl
      }
    }
  }
`)

const MARK_NOTIFICATION_AS_READ_MUTATION = gql(`
  mutation MarkNotificationAsRead($userNotificationId: ID!) {
    markNotificationAsRead(input: { userNotificationId: $userNotificationId }) {
      userNotification {
        id
        readAt
      }
      errors
    }
  }
`)

export const NotificationPopover = () => {
  const { push } = useRouter()
  const { t } = useTranslation()
  const { data } = useQuery(GET_UNREAD_FIRST_NOTIFICATION_QUERY)
  const { data: notificationsData } = useQuery(GET_THREE_NOTIFICATIONS_QUERY)
  const [hover, setHover] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setPopoverOpen(false))

  const clickBell = () => {
    isMobile ? push('/notifications') : setPopoverOpen((prev) => !prev)
  }

  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ_MUTATION)

  return (
    <Box ref={ref} sx={{ position: 'relative' }}>
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={clickBell}
        sx={{
          transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
          cursor: 'pointer',
          color: data?.notifications?.nodes?.length
            ? hover
              ? Colors['Samansa_Yellow/500']
              : Colors['Samansa_Yellow/600']
            : hover
            ? Colors['Gray/350']
            : Colors['Gray/400'],
        }}
      >
        <Icon name='bell-dot' size={isMobile ? 20 : 32} />
      </Box>
      {popoverOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: '343px',
            bgcolor: Colors['Gray/1000'],
            boxShadow: Shadows['200'],
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
          }}
        >
          {notificationsData?.notifications?.nodes?.map((notification) => (
            <Box
              key={notification.id}
              component='a'
              href={notification.linkUrl}
              onClick={() => {
                markNotificationAsRead({ variables: { userNotificationId: notification.id } })
              }}
              sx={{
                width: '100%',
                padding: 1.5,
                display: 'flex',
                gap: 1.5,
                cursor: 'pointer',
                color: Colors['Base/White'],
                textDecoration: 'none',
                transition: 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)',
                '&:hover': {
                  backgroundColor: Colors['Gray/900'],
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '50%',
                  overflow: 'hidden',
                }}
              >
                <Avatar
                  src={notification.avatarUrl || undefined}
                  alt={notification.title}
                  sx={{ width: '100%', height: '100%' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: Colors['White/Alpha/08'],
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography name={'Body/14/Medium'}>{notification.title}</Typography>
                  {!notification.readAt && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: Colors['Samansa_Yellow/500'],
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
                <Box>
                  <Typography name={'Body/10/Regular'}>{notification.message}</Typography>
                </Box>
              </Box>
              {notification.thumbnailUrl && (
                <Box
                  width={96}
                  height={54}
                  sx={{
                    position: 'relative',
                    borderRadius: '4px',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Image
                    src={notification.thumbnailUrl}
                    alt=''
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
              )}
            </Box>
          ))}
          <Box
            sx={{
              width: '100%',
              padding: 0.5,
              display: 'flex',
              justifyContent: 'center',
              borderTop: `1px solid ${Colors['Gray/900']}`,
            }}
          >
            <Button
              variant='text'
              size='small'
              endIcon='chevron-right'
              color='secondary'
              onClick={() => push('/notifications')}
            >
              {t('See all')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
